/**
 * LazyAuthoringDemoDialog (#4231) - the "create your own" dialog, fetched on
 * first open instead of riding in the entry chunk.
 *
 * AuthoringDemoDialog statically pulls HybridScenarioChat / GatheringChat and,
 * through MarkdownRenderer, react-syntax-highlighter (refractor alone was 591 KB
 * of the 1.77 MB entry). HomePageV2 and GuestDiscoveryContextsPage are in the
 * entry (the guest landing path), so they mount THIS wrapper, which imports the
 * dialog only when the learner first opens it.
 *
 * It is optional UI, so a failed load (flaky network, or a chunk renamed by a
 * deploy) must not reload the page nor reach the error boundary: the dialog
 * stays closed and a toast offers Reload, same rule as the Help panel. The
 * dialog chunk carries CSS, so ONE import promise is kept per document (a retried
 * import() can resolve the JS without its stylesheet): a failed load stays
 * failed until the learner reloads, and every later open says so again.
 *
 * Once loaded the dialog stays mounted (as it was before), so its own
 * open/close handling is unchanged.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastSafe } from '../contexts/ToastContext';
import { importOptional, reportOptionalChunkFailure } from '../lib/importOptional';

let dialogImport = null;
export function loadAuthoringDemoDialog() {
  if (!dialogImport) dialogImport = importOptional(() => import('./AuthoringDemoDialog'), 'AuthoringDemoDialog');
  return dialogImport;
}

/** Test seam: forget the per-document import (a real reload does this for free). */
export function _resetAuthoringDemoDialogForTests() {
  dialogImport = null;
}

function LazyAuthoringDemoDialog(props) {
  const { isOpen, onClose } = props;
  const { showToast } = useToastSafe();
  const { t } = useTranslation();
  const [Dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || Dialog) return undefined;
    let cancelled = false;
    setLoading(true);
    loadAuthoringDemoDialog().then(
      (mod) => {
        if (cancelled) return;
        setLoading(false);
        setDialog(() => mod.default);
      },
      () => {
        if (cancelled) return;
        setLoading(false);
        if (onClose) onClose();
        reportOptionalChunkFailure(showToast, t);
      },
    );
    return () => { cancelled = true; };
    // onClose is the parent's inline arrow (new each render): it is only read in the failure branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, Dialog, showToast, t]);

  if (Dialog) return <Dialog {...props} />;
  if (isOpen && loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.35)' }}
      >
        <div className="loading-spinner" />
      </div>
    );
  }
  return null;
}

export default LazyAuthoringDemoDialog;
